// A1 ↔ {col,row} translation helpers and range expansion. Internal coords 0-indexed.

export interface Coord {
  /** 0-indexed column. `A` → 0, `Z` → 25, `AA` → 26. */
  col: number;
  /** 0-indexed row. Row `1` in A1 notation → 0 internally. */
  row: number;
}

/** "A" → 0, "Z" → 25, "AA" → 26, "AB" → 27, "AZ" → 51, "BA" → 52, etc. */
export const colLettersToIndex = (letters: string): number => {
  let n = 0;
  for (let i = 0; i < letters.length; i++) {
    // base-26 with no zero digit
    n = n * 26 + (letters.charCodeAt(i) - 64);
  }
  return n - 1;
};

/** 0 → "A", 25 → "Z", 26 → "AA", 27 → "AB". Inverse of colLettersToIndex. */
export const colIndexToLetters = (index: number): string => {
  let n = index;
  let s = "";
  let done = false;
  while (!done) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    if (n < 26) {
      done = true;
    } else {
      n = Math.floor(n / 26) - 1;
    }
  }
  return s;
};

/** Format a Coord as A1-notation. `{col:0,row:0}` → `"A1"`. */
export const coordToA1 = ({ col, row }: Coord): string =>
  `${colIndexToLetters(col)}${row + 1}`;

/**
 * Enumerate every Coord in the rectangle with corners `start`/`end`, row-major.
 * Corners are normalized, so either ordering yields the same set.
 */
export const expandRange = (start: Coord, end: Coord): Coord[] => {
  const minRow = Math.min(start.row, end.row);
  const maxRow = Math.max(start.row, end.row);
  const minCol = Math.min(start.col, end.col);
  const maxCol = Math.max(start.col, end.col);
  const out: Coord[] = [];
  for (let r = minRow; r <= maxRow; r++) {
    for (let c = minCol; c <= maxCol; c++) {
      out.push({ row: r, col: c });
    }
  }
  return out;
};

/** Cell count for the range with corners `start` and `end`. */
export const rangeCellCount = (start: Coord, end: Coord): number => {
  const rows = Math.abs(start.row - end.row) + 1;
  const cols = Math.abs(start.col - end.col) + 1;
  return rows * cols;
};
